import React, { useEffect, useMemo, useRef, useState } from "react";
import { STAGE_OPTIONS, STAGE_LABELS, ENROLLMENT_OPTIONS, ENROLLMENT_LABELS } from "../constants";
import {
  classNames,
  formatDueLabel,
  formatRelative,
  primaryContactEmail,
  stageTone,
} from "./utils";
import SnoozePopover from "./SnoozePopover";

const STAGE_BADGE_TONE = {
  success: "bg-emerald-50 text-emerald-700 ring-emerald-200/60",
  warning: "bg-amber-50 text-amber-700 ring-amber-200/60",
  info: "bg-sky-50 text-sky-700 ring-sky-200/60",
  danger: "bg-red-50 text-red-700 ring-red-200/60",
  default: "bg-zinc-100 text-zinc-700 ring-zinc-200",
};

function StageBadge({ stage }) {
  const tone = stageTone(stage);
  return (
    <span
      className={classNames(
        "inline-flex items-center rounded-md px-1.5 py-0.5 text-[11px] font-medium ring-1 ring-inset",
        STAGE_BADGE_TONE[tone] || STAGE_BADGE_TONE.default
      )}
    >
      {STAGE_LABELS[stage] || stage?.replace(/_/g, " ") || "—"}
    </span>
  );
}

function RowSnoozeButton({ row, onSnooze }) {
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        ref={anchorRef}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        className="rounded-md border border-zinc-200 bg-white px-2 py-1 text-[11px] font-medium text-zinc-600 hover:border-zinc-300 hover:bg-zinc-50"
      >
        Snooze
      </button>
      {open ? (
        <div onClick={(e) => e.stopPropagation()}>
          <SnoozePopover
            anchorRef={anchorRef}
            align="right"
            onSelect={(date) => onSnooze?.(row, date)}
            onClose={() => setOpen(false)}
          />
        </div>
      ) : null}
    </div>
  );
}

export default function LeadsLedger({
  rows,
  loading,
  pagination,
  page,
  filters,
  onFiltersChange,
  onPageChange,
  onOpenLead,
  onPatchCampaignLead,
  onSnooze,
  onSaveSegment,
  selectedRowId,
  onBulkStage,
  onBulkSnooze,
  onBulkEnrollment,
  emptyTitle = "No leads match these filters",
  emptyBody = "Adjust the filters, clear the search, or import a CSV to populate this campaign.",
  activeSegmentLabel,
  onClearSegment,
}) {
  const [selectedIds, setSelectedIds] = useState(new Set());
  const listRef = useRef(null);

  useEffect(() => {
    setSelectedIds((current) => {
      const next = new Set();
      for (const row of rows || []) {
        if (current.has(row.campaignLead?._id)) next.add(row.campaignLead._id);
      }
      return next;
    });
  }, [rows]);

  const allSelected = rows?.length && selectedIds.size === rows.length;

  const toggleAll = () => {
    setSelectedIds(allSelected ? new Set() : new Set((rows || []).map((r) => r.campaignLead._id)));
  };

  const toggleOne = (id) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectedArray = useMemo(() => Array.from(selectedIds), [selectedIds]);

  return (
    <div className="flex h-full flex-col">
      <div className="flex flex-wrap items-center gap-2 border-b border-zinc-200 bg-white px-5 py-3">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <input
            type="text"
            data-search-input
            value={filters.q}
            onChange={(event) => onFiltersChange({ ...filters, q: event.target.value })}
            placeholder="Search company, email, website…"
            className="w-full rounded-lg border border-zinc-300 bg-white px-3 py-1.5 text-sm placeholder:text-zinc-400 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
          />
        </div>
        <select
          value={filters.stage}
          onChange={(event) => onFiltersChange({ ...filters, stage: event.target.value })}
          className="rounded-lg border border-zinc-300 bg-white px-2.5 py-1.5 text-sm text-zinc-700 focus:border-indigo-500 focus:outline-none"
        >
          <option value="">All stages</option>
          {STAGE_OPTIONS.map((stage) => (
            <option key={stage} value={stage}>
              {STAGE_LABELS[stage]}
            </option>
          ))}
        </select>
        <select
          value={filters.status}
          onChange={(event) => onFiltersChange({ ...filters, status: event.target.value })}
          className="rounded-lg border border-zinc-300 bg-white px-2.5 py-1.5 text-sm text-zinc-700 focus:border-indigo-500 focus:outline-none"
        >
          <option value="">All enrollment</option>
          {ENROLLMENT_OPTIONS.map((status) => (
            <option key={status} value={status}>
              {ENROLLMENT_LABELS[status]}
            </option>
          ))}
        </select>
        <input
          value={filters.tags}
          onChange={(event) => onFiltersChange({ ...filters, tags: event.target.value })}
          placeholder="Tags"
          className="w-32 rounded-lg border border-zinc-300 bg-white px-2.5 py-1.5 text-sm placeholder:text-zinc-400 focus:border-indigo-500 focus:outline-none"
        />
        <label className="inline-flex items-center gap-1.5 rounded-lg border border-zinc-300 bg-white px-2.5 py-1.5 text-sm text-zinc-700">
          <input
            type="checkbox"
            checked={filters.needsAction}
            onChange={(event) =>
              onFiltersChange({ ...filters, needsAction: event.target.checked })
            }
            className="h-3.5 w-3.5 rounded border-zinc-300 text-indigo-600"
          />
          Needs action
        </label>

        {activeSegmentLabel ? (
          <span className="inline-flex items-center gap-2 rounded-lg bg-indigo-50 px-2 py-1 text-xs font-medium text-indigo-700">
            Segment: {activeSegmentLabel}
            <button
              onClick={onClearSegment}
              className="text-indigo-500 hover:text-indigo-700"
              title="Clear segment"
            >
              ×
            </button>
          </span>
        ) : null}

        <div className="ml-auto flex items-center gap-2">
          <button
            onClick={() => {
              const name = window.prompt("Name this segment");
              if (name) onSaveSegment?.(name);
            }}
            className="rounded-lg border border-zinc-300 bg-white px-2.5 py-1.5 text-xs font-medium text-zinc-600 hover:bg-zinc-50"
          >
            Save segment
          </button>
        </div>
      </div>

      {selectedIds.size ? (
        <div className="flex flex-wrap items-center gap-2 border-b border-indigo-100 bg-indigo-50/60 px-5 py-2 text-sm">
          <span className="font-medium text-indigo-900">{selectedIds.size} selected</span>
          <span className="h-4 w-px bg-indigo-200" />
          <select
            onChange={(event) => {
              const value = event.target.value;
              if (!value) return;
              onBulkStage?.(selectedArray, value);
              event.target.value = "";
            }}
            defaultValue=""
            className="rounded-lg border border-indigo-200 bg-white px-2 py-1 text-xs text-indigo-900 focus:outline-none"
          >
            <option value="">Set stage…</option>
            {STAGE_OPTIONS.map((stage) => (
              <option key={stage} value={stage}>
                {STAGE_LABELS[stage]}
              </option>
            ))}
          </select>
          <select
            onChange={(event) => {
              const value = event.target.value;
              if (!value) return;
              onBulkEnrollment?.(selectedArray, value);
              event.target.value = "";
            }}
            defaultValue=""
            className="rounded-lg border border-indigo-200 bg-white px-2 py-1 text-xs text-indigo-900 focus:outline-none"
          >
            <option value="">Set enrollment…</option>
            {ENROLLMENT_OPTIONS.map((status) => (
              <option key={status} value={status}>
                {ENROLLMENT_LABELS[status]}
              </option>
            ))}
          </select>
          <button
            onClick={() => onBulkSnooze?.(selectedArray, 3)}
            className="rounded-lg border border-indigo-200 bg-white px-2.5 py-1 text-xs font-medium text-indigo-900 hover:bg-indigo-100"
          >
            Snooze 3d
          </button>
          <button
            onClick={() => onBulkSnooze?.(selectedArray, 7)}
            className="rounded-lg border border-indigo-200 bg-white px-2.5 py-1 text-xs font-medium text-indigo-900 hover:bg-indigo-100"
          >
            Snooze 7d
          </button>
          <button
            onClick={() => setSelectedIds(new Set())}
            className="ml-auto text-xs font-medium text-indigo-700 hover:underline"
          >
            Clear selection
          </button>
        </div>
      ) : null}

      <div
        ref={listRef}
        className="flex-1 overflow-y-auto bg-zinc-50/40"
        data-lead-ledger
      >
        <div className="sticky top-0 z-10 grid grid-cols-[40px,1.8fr,0.9fr,0.9fr,1fr,0.9fr,0.6fr] items-center gap-3 border-b border-zinc-200 bg-white px-5 py-2 text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={Boolean(allSelected)}
              onChange={toggleAll}
              className="h-3.5 w-3.5 rounded border-zinc-300 text-indigo-600"
            />
          </label>
          <span>Lead</span>
          <span>Stage</span>
          <span>Enrollment</span>
          <span>Next action</span>
          <span>Last touch</span>
          <span className="text-right">Actions</span>
        </div>

        {loading && !rows?.length ? (
          <div className="flex h-48 items-center justify-center text-sm text-zinc-500">
            Loading leads…
          </div>
        ) : rows?.length ? (
          <div>
            {rows.map((row) => {
              const cl = row.campaignLead || {};
              const selected = selectedIds.has(cl._id);
              const isActive = selectedRowId === cl._id;
              return (
                <div
                  key={cl._id}
                  onClick={() => onOpenLead?.(row)}
                  className={classNames(
                    "grid cursor-pointer grid-cols-[40px,1.8fr,0.9fr,0.9fr,1fr,0.9fr,0.6fr] items-center gap-3 border-b border-zinc-100 px-5 py-2.5 transition-colors",
                    isActive
                      ? "bg-indigo-50/60"
                      : selected
                        ? "bg-indigo-50/30"
                        : "bg-white hover:bg-zinc-50"
                  )}
                >
                  <label className="flex items-center" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selected}
                      onChange={() => toggleOne(cl._id)}
                      className="h-3.5 w-3.5 rounded border-zinc-300 text-indigo-600"
                    />
                  </label>

                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-zinc-900">
                      {row.lead?.company_name || "Untitled lead"}
                    </p>
                    <p className="truncate text-xs text-zinc-500">
                      {primaryContactEmail(row.lead) || row.lead?.website || "No contact info"}
                    </p>
                  </div>

                  <div onClick={(e) => e.stopPropagation()}>
                    <select
                      value={cl.stage || ""}
                      onChange={(event) =>
                        onPatchCampaignLead?.(cl._id, { stage: event.target.value })
                      }
                      className="w-full rounded-md border border-transparent bg-transparent px-1 py-0.5 text-xs font-medium text-zinc-700 hover:border-zinc-300 hover:bg-white focus:border-indigo-500 focus:bg-white focus:outline-none"
                    >
                      {STAGE_OPTIONS.map((stage) => (
                        <option key={stage} value={stage}>
                          {STAGE_LABELS[stage]}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div onClick={(e) => e.stopPropagation()}>
                    <select
                      value={cl.enrollmentStatus || ""}
                      onChange={(event) =>
                        onPatchCampaignLead?.(cl._id, {
                          enrollmentStatus: event.target.value,
                        })
                      }
                      className="w-full rounded-md border border-transparent bg-transparent px-1 py-0.5 text-xs font-medium text-zinc-700 hover:border-zinc-300 hover:bg-white focus:border-indigo-500 focus:bg-white focus:outline-none"
                    >
                      {ENROLLMENT_OPTIONS.map((status) => (
                        <option key={status} value={status}>
                          {ENROLLMENT_LABELS[status]}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="text-xs text-zinc-700">
                    {cl.nextActionAt ? (
                      <span
                        className={classNames(
                          "tabular-nums",
                          new Date(cl.nextActionAt) < new Date() ? "text-red-600 font-medium" : ""
                        )}
                      >
                        {formatDueLabel(cl.nextActionAt)}
                      </span>
                    ) : (
                      <span className="text-zinc-400">Not scheduled</span>
                    )}
                  </div>

                  <div className="text-xs text-zinc-500">
                    {formatRelative(row.lastActivity?.occurredAt || row.lead?.updatedAt)}
                  </div>

                  <div
                    className="flex items-center justify-end gap-1.5"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <StageBadge stage={cl.stage} />
                    <RowSnoozeButton row={row} onSnooze={onSnooze} />
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="flex h-full min-h-[240px] flex-col items-center justify-center px-6 text-center">
            <p className="text-base font-semibold text-zinc-900">{emptyTitle}</p>
            <p className="mt-1 max-w-sm text-sm text-zinc-500">{emptyBody}</p>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between border-t border-zinc-200 bg-white px-5 py-2 text-xs text-zinc-500">
        <span>
          {pagination.total
            ? `Showing ${(pagination.page - 1) * pagination.limit + 1}-${Math.min(
                pagination.page * pagination.limit,
                pagination.total
              )} of ${pagination.total}`
            : "No results"}
        </span>
        <div className="flex items-center gap-2">
          <button
            disabled={page <= 1}
            onClick={() => onPageChange(Math.max(1, page - 1))}
            className="rounded-md border border-zinc-200 bg-white px-2.5 py-1 disabled:cursor-not-allowed disabled:text-zinc-300"
          >
            Previous
          </button>
          <span className="tabular-nums">
            {pagination.page} / {pagination.pages || 1}
          </span>
          <button
            disabled={page >= (pagination.pages || 1)}
            onClick={() => onPageChange(Math.min(pagination.pages, page + 1))}
            className="rounded-md border border-zinc-200 bg-white px-2.5 py-1 disabled:cursor-not-allowed disabled:text-zinc-300"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}
